import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { notify } from "../../components/ui/notify";
import { Spin, Button, Select } from "antd";
import { HappyProvider } from "@ant-design/happy-work-theme";

import {
  actionAutoridadCreate,
  actionAutoridadGetByCode,
  actionAutoridadUpdateByCode,
} from "../../redux/actions/autoridades/autoridades";
import { PATH } from "../../redux/utils";
import "../../components/forms/tribunal/FormTribunal.css";

const api = axios.create({
  baseURL: PATH,
  headers: { "Content-Type": "application/json" },
});

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const collapseSpaces = (s) => String(s ?? "").normalize("NFC").replace(/\s+/g, " ").trim();

export default function CrearAutoridadPage() {
  const { idAutoridad } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isEdit = Boolean(idAutoridad);

  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [detalle, setDetalle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [ciudadesOptions, setCiudadesOptions] = useState([]);
  const [estadosOptions, setEstadosOptions] = useState([]);
  const [tiposOptions, setTiposOptions] = useState([]);

  const [form, setForm] = useState({ nombre: "", id_ciudad: null, id_estado: null, id_tipo_autoridad: null });

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => { const c = { ...e }; delete c[name]; return c; });
  };

  useEffect(() => {
    api
      .get("ciudades", { headers: authHeader() })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setCiudadesOptions(arr.map((c) => ({ label: c.nombre, value: c.id_ciudad ?? c.id })));
      })
      .catch(() => {});

    api
      .get("estados", { headers: authHeader() })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setEstadosOptions(arr.map((e) => ({ label: e.nombre, value: e.id_estado ?? e.id })));
      })
      .catch(() => {});

    api
      .get("tipo_autoridades", { headers: authHeader() })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setTiposOptions(arr.map((t) => ({ label: t.nombre, value: t.id_tipo_autoridad ?? t.id })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingEdit(true);
        const data = await dispatch(actionAutoridadGetByCode(idAutoridad));
        if (!mounted) return;
        setDetalle(data || null);
        setForm({
          nombre: data?.nombre_autoridad ?? data?.nombre ?? "",
          id_ciudad: data?.id_ciudad ?? null,
          id_estado: data?.id_estado ?? null,
          id_tipo_autoridad: data?.id_tipo_autoridad ?? null,
        });
      } catch (e) {
        if (!mounted) return;
        notify.error({
          message: "No se pudo cargar la autoridad",
          description: e?.response?.data?.detail || e?.message || "Sin detalle",
          placement: "bottomRight",
        });
        setDetalle(null);
      } finally {
        if (mounted) setLoadingEdit(false);
      }
    })();
    return () => { mounted = false; };
  }, [dispatch, isEdit, idAutoridad]);

  const handleBack = () =>
    navigate(isEdit ? `/autoridades/${idAutoridad}` : "/autoridades");

  const validate = () => {
    const e = {};
    if (!collapseSpaces(form.nombre)) e.nombre = "Campo requerido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = {
      nombre: collapseSpaces(form.nombre),
      id_ciudad: form.id_ciudad ?? null,
      id_estado: form.id_estado ?? null,
      id_tipo_autoridad: form.id_tipo_autoridad ?? null,
    };

    try {
      setSubmitting(true);
      if (isEdit) {
        await dispatch(actionAutoridadUpdateByCode(idAutoridad, payload));
        notify.success({ message: "Autoridad actualizada", placement: "bottomRight" });
        navigate(`/autoridades/${idAutoridad}`);
      } else {
        const res = await dispatch(actionAutoridadCreate(payload));
        const newId = res?.id_autoridad ?? res?.id;
        notify.success({ message: "Autoridad creada exitosamente", placement: "bottomRight" });
        navigate(newId ? `/autoridades/${newId}` : "/autoridades");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al guardar";
      notify.error({ message: "No se pudo guardar", description: msg, placement: "bottomRight" });
    } finally {
      setSubmitting(false);
    }
  };

  const ready = !isEdit || !!detalle;

  return (
    <HappyProvider>
      <div className="oa-exp-page">
        {/* ── TOPBAR ─────────────────────────────────────────────────── */}
        <section className="oa-exp-topbar">
          <div className="oa-exp-topbar-left">
            <div className="oa-exp-title">
              {isEdit ? "Editando Autoridad" : "Creando Autoridad"}
            </div>
            <div className="oa-exp-subtitle">Catálogo de Autoridades</div>
          </div>
          <div className="oa-exp-topbar-right">
            <Button className="oa-exp-btn oa-exp-btn-ghost" onClick={handleBack} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              className="oa-exp-btn oa-exp-btn-primary"
              disabled={submitting}
              loading={submitting}
              onClick={handleSubmit}
              icon={
                <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                  <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L400 60.7C388 48.7 371.7 42 354.5 42H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V96zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
                </svg>
              }
            >
              {isEdit ? "Guardar cambios" : "Crear autoridad"}
            </Button>
          </div>
        </section>

        {/* ── BODY ───────────────────────────────────────────────────── */}
        <main className="oa-exp-main">
          {(!ready || loadingEdit) ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : (
            <section className="oa-card">
              <div className="oa-card-head">
                <h2 className="oa-card-title">
                  <span className="oa-card-icon">
                    <i className="fa-solid fa-landmark" />
                  </span>
                  DATOS DE LA AUTORIDAD
                </h2>
              </div>

              <div className="oa-card-body oa-grid-3">
                <div className="oa-field">
                  <label className="oa-label">
                    <span className="oa-req">*</span> Nombre
                  </label>
                  <input
                    className={`oa-input ${errors.nombre ? "oa-input-error" : ""}`}
                    type="text"
                    placeholder="Ej. Junta Especial No. 5"
                    maxLength={255}
                    value={form.nombre}
                    onChange={(e) => setField("nombre", e.target.value)}
                  />
                  {errors.nombre && <div className="oa-error">{errors.nombre}</div>}
                </div>

                <div className="oa-field">
                  <label className="oa-label">Tipo de autoridad</label>
                  <Select
                    placeholder="Selecciona un tipo..."
                    options={tiposOptions}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    style={{ width: "100%" }}
                    value={form.id_tipo_autoridad}
                    onChange={(value) => setField("id_tipo_autoridad", value ?? null)}
                  />
                </div>

                <div className="oa-field">
                  <label className="oa-label">Estado</label>
                  <Select
                    placeholder="Selecciona un estado..."
                    options={estadosOptions}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    style={{ width: "100%" }}
                    value={form.id_estado}
                    onChange={(value) => setField("id_estado", value ?? null)}
                  />
                </div>

                <div className="oa-field">
                  <label className="oa-label">Ciudad</label>
                  <Select
                    placeholder="Selecciona una ciudad..."
                    options={ciudadesOptions}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    style={{ width: "100%" }}
                    value={form.id_ciudad}
                    onChange={(value) => setField("id_ciudad", value ?? null)}
                  />
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    </HappyProvider>
  );
}
